/** Produit catalogue — aligné sur `products.php`. */
import { PCG_SALES_DEFAULT } from "../../pcg/pcg";
import { opKindOf, type OpKind } from "../../legal/opNature";

export type TaxRate = {
  id: number;
  label: string;
  rate_bp: number;
  is_default: boolean;
};

export type Product = {
  id: number;
  company_id: number;
  label: string;
  description: string | null;
  product_code: string | null;
  unit: string;
  unit_price_ht_cents: number;
  cost_ht_cents: number;
  default_qty: number;
  pcg_code: string;
  pcg_sub: string | null;
  tax_rate_id: number | null;
  op_kind: OpKind;
  active: boolean | number;
  created_at: string;
};

export type ProductDraft = {
  id?: number;
  label: string;
  description: string;
  product_code: string;
  unit: string;
  price: string;
  cost: string;
  default_qty: number;
  pcg_code: string;
  pcg_sub: string;
  tax_rate_id: number | null;
  op_kind: OpKind;
  active: boolean;
};

export function emptyDraft(defaultTaxId: number | null): ProductDraft {
  return {
    label: "",
    description: "",
    product_code: "",
    unit: "u",
    price: "",
    cost: "",
    default_qty: 1,
    pcg_code: PCG_SALES_DEFAULT,
    pcg_sub: "",
    tax_rate_id: defaultTaxId,
    op_kind: "service",
    active: true,
  };
}

export function productToCopyDraft(p: Product, decimalComma: boolean): ProductDraft {
  const d = productToDraft(p, decimalComma);
  delete d.id;
  d.active = true;
  const base = (p.label || "").replace(/\s*\(copie\)\s*$/i, "").trim() || "Produit";
  d.label = `${base} (copie)`;
  return d;
}

export function productToDraft(p: Product, decimalComma: boolean): ProductDraft {
  return {
    id: p.id,
    label: p.label,
    description: p.description || "",
    product_code: p.product_code || "",
    unit: p.unit || "u",
    price: centsToInput(Number(p.unit_price_ht_cents) || 0, decimalComma),
    cost: centsToInput(Number(p.cost_ht_cents) || 0, decimalComma),
    default_qty: Math.min(9999, Math.max(1, Number(p.default_qty) || 1)),
    pcg_code: p.pcg_code || PCG_SALES_DEFAULT,
    pcg_sub: p.pcg_sub || "",
    tax_rate_id: p.tax_rate_id === null || p.tax_rate_id === undefined ? null : Number(p.tax_rate_id),
    op_kind: opKindOf(p.op_kind),
    active: Number(p.active) === 1 || p.active === true,
  };
}

export function centsToInput(cents: number, decimalComma: boolean): string {
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const euros = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, "0");
  return `${neg ? "−" : ""}${euros}${decimalComma ? "," : "."}${frac}`;
}

/** Le point du pavé numérique s’affiche en virgule quand la société est en virgule. */
export function typedDecimal(raw: string, decimalComma: boolean): string {
  return decimalComma ? raw.replace(/\./g, ",") : raw;
}

/**
 * Dernier « . » ou « , » = décimale. L’autre, s’il est présent, sépare les milliers.
 * 12.50 et 12,50 valent donc 12,50.
 */
export function normalizeDecimal(raw: string): string {
  const t = raw.trim().replace(/\u00a0/g, "").replace(/\s/g, "").replace(/−/g, "-").replace(/€/g, "").replace(/%/g, "");
  const lastComma = t.lastIndexOf(",");
  const lastDot = t.lastIndexOf(".");
  if (lastComma < 0 && lastDot < 0) return t;
  const dec = Math.max(lastComma, lastDot);
  const intPart = t.slice(0, dec).replace(/[.,]/g, "");
  const frac = t.slice(dec + 1).replace(/[.,]/g, "");
  return `${intPart}.${frac}`;
}

export function parseQty(raw: string | number | null | undefined): number {
  const n = Number(normalizeDecimal(String(raw ?? "")));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function inputToCents(raw: string, _decimalComma: boolean): number | null {
  const t = raw.trim().replace(/\u00a0/g, "").replace(/\s/g, "").replace(/−/g, "-").replace(/€/g, "");
  if (t === "") return 0;
  const normalized = normalizeDecimal(t).replace(/\.$/, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export function asProduct(row: Product): Product {
  return {
    ...row,
    id: Number(row.id),
    company_id: Number(row.company_id),
    description: row.description ?? null,
    product_code: row.product_code ?? null,
    unit_price_ht_cents: Number(row.unit_price_ht_cents) || 0,
    cost_ht_cents: Number(row.cost_ht_cents) || 0,
    default_qty: Math.min(9999, Math.max(1, Number(row.default_qty) || 1)),
    pcg_code: row.pcg_code || PCG_SALES_DEFAULT,
    pcg_sub: row.pcg_sub ?? null,
    tax_rate_id: row.tax_rate_id === null || row.tax_rate_id === undefined ? null : Number(row.tax_rate_id),
    op_kind: opKindOf(row.op_kind),
    active: Number(row.active) === 1 || row.active === true,
  };
}
